import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { OrderDetails } from '../../services/orders.service';
import ordersService from '../../services/orders.service';
import { useToast } from '../../contexts/ToastContext';
import PermissionsContainer from '../PermissionsContainer';
import {
  computeRailBillingEligibility,
  railBillingCheck,
} from '../../utils/railBillingEligibility';

interface RailBillingActionsProps {
  orderDetails: OrderDetails;
  /** When true, all buttons are disabled — used to defer rail-billing actions
   * while an unrelated save is in flight. */
  busy?: boolean;
  /** Invoked after a successful send/cancel so the page can re-fetch and
   * reflect the new rail-billing state. */
  onRefresh?: () => void;
}

/**
 * Footer rail-billing action buttons. Mirrors v1 lib_editrecord.pl's
 * `$inlinerailbillbutton` block:
 *  - "Send Rail Billing" (+ EMPTY/LOADED selector) when required & eligible
 *  - "Railbill Not Req." (disabled) when eligible but not required
 *  - "Cancel Rail Billing" when already sent and not terminated
 *
 * Gated by the `rail-billing` PermissionsContainer (hidden by default), same
 * pattern as OrderReuseActions.
 */
const RailBillingActions: React.FC<RailBillingActionsProps> = ({
  orderDetails,
  busy = false,
  onRefresh,
}) => {
  const { showToast } = useToast();

  const eligibility = computeRailBillingEligibility(orderDetails);

  const [loadedEmpty, setLoadedEmpty] = useState<'EMPTY' | 'LOADED'>(
    eligibility.defaultLoadedEmpty,
  );
  const [sending, setSending] = useState(false);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  // Keep the dropdown default in sync if the move type changes underneath us.
  useEffect(() => {
    setLoadedEmpty(eligibility.defaultLoadedEmpty);
  }, [eligibility.defaultLoadedEmpty]);

  const cancelDialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dlg = cancelDialogRef.current;
    if (!dlg) return;
    if (cancelConfirmOpen && !dlg.open) dlg.showModal();
    else if (!cancelConfirmOpen && dlg.open) dlg.close();
  }, [cancelConfirmOpen]);

  const errMessage = (err: unknown): string => {
    const e = err as { response?: { data?: { detail?: unknown } }; message?: string };
    const detail = e?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    return e?.message || 'Request failed';
  };

  const handleSend = useCallback(async () => {
    // v1 railBillingCheck() — block and list missing fields before sending.
    const validation = railBillingCheck(orderDetails);
    if (!validation.ok) {
      const fields = validation.missing.join(', ');
      showToast({
        type: 'warning',
        message: `Rail Billing requires: ${fields}`,
        duration: 7000,
      });
      return;
    }

    setSending(true);
    try {
      const res = await ordersService.sendRailBilling(orderDetails.pronumber, loadedEmpty);
      showToast({ type: res.success ? 'success' : 'info', message: res.message });
      onRefresh?.();
    } catch (err) {
      showToast({ type: 'error', message: `Send rail billing failed: ${errMessage(err)}` });
    } finally {
      setSending(false);
    }
  }, [orderDetails, loadedEmpty, onRefresh, showToast]);

  const handleCancel = useCallback(async () => {
    setCancelling(true);
    try {
      const res = await ordersService.cancelRailBilling(orderDetails.pronumber);
      showToast({ type: res.success ? 'success' : 'info', message: res.message });
      setCancelConfirmOpen(false);
      onRefresh?.();
    } catch (err) {
      showToast({ type: 'error', message: `Cancel rail billing failed: ${errMessage(err)}` });
    } finally {
      setCancelling(false);
    }
  }, [orderDetails.pronumber, onRefresh, showToast]);

  return (
    <>
      {eligibility.canSend && (
        <PermissionsContainer
          componentId="rail-billing"
          componentName="Rail Billing"
          defaultHidden
        >
          <div className="inline-flex items-center gap-2">
            <button
              type="button"
              onClick={handleSend}
              disabled={busy || sending}
              className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm rounded-md transition-colors"
            >
              {sending ? 'Sending…' : 'Send Rail Billing'}
            </button>
            <select
              value={loadedEmpty}
              onChange={(e) => setLoadedEmpty(e.target.value as 'EMPTY' | 'LOADED')}
              disabled={busy || sending}
              title="Send the rail bill as EMPTY or LOADED"
              className="px-2 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-gray-700 disabled:bg-gray-100"
            >
              <option value="EMPTY">EMPTY</option>
              <option value="LOADED">LOADED</option>
            </select>
          </div>
        </PermissionsContainer>
      )}

      {eligibility.showNotRequired && (
        <PermissionsContainer
          componentId="rail-billing"
          componentName="Rail Billing"
          defaultHidden
        >
          <button
            type="button"
            disabled
            title="This move does not require rail billing"
            className="px-4 py-1.5 bg-gray-200 text-gray-500 text-sm rounded-md cursor-not-allowed"
          >
            Railbill Not Req.
          </button>
        </PermissionsContainer>
      )}

      {eligibility.canCancel && (
        <PermissionsContainer
          componentId="rail-billing"
          componentName="Rail Billing"
          defaultHidden
        >
          <button
            type="button"
            onClick={() => setCancelConfirmOpen(true)}
            disabled={busy || cancelling}
            className="px-4 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm rounded-md transition-colors"
          >
            {cancelling ? 'Cancelling…' : 'Cancel Rail Billing'}
          </button>
        </PermissionsContainer>
      )}

      <dialog
        ref={cancelDialogRef}
        onCancel={(e) => {
          e.preventDefault();
          if (!cancelling) setCancelConfirmOpen(false);
        }}
        className="bg-transparent p-0 backdrop:bg-gray-900/50 backdrop:backdrop-blur-sm rounded-lg"
      >
        <div className="bg-panel rounded-lg shadow-xl p-6 max-w-sm w-full mx-4">
          <h3 className="text-base font-semibold text-heading mb-2">
            Cancel Rail Billing
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            This will email the carrier a cancellation request for PRO{' '}
            {orderDetails.pronumber} and clear the sent rail bill. Continue?
          </p>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => setCancelConfirmOpen(false)}
              disabled={cancelling}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Keep
            </button>
            <button
              type="button"
              onClick={handleCancel}
              disabled={cancelling}
              className="px-4 py-2 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 rounded-md"
            >
              {cancelling ? 'Cancelling…' : 'Cancel Rail Billing'}
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
};

export default RailBillingActions;
